import { getContentUrlPath, getSubDomain } from "@platformx/utilities";

/**
 * Shallow-removes properties whose value carries no information — `undefined`,
 * `null`, `""`, `[]` and `{}`.
 *
 * The business rule the SEO audit generalises across every content type: never
 * emit a node that carries only its `@type`. `false` and `0` are meaningful
 * values and are kept.
 */
export const omitEmpty = <T extends Record<string, any>>(node: T): Partial<T> => {
  const result: Record<string, any> = {};
  Object.entries(node || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value as Record<string, any>).length === 0
    ) {
      return;
    }
    result[key] = value;
  });
  return result as Partial<T>;
};

/**
 * Brand- and route-aware delivery URL for a content item, e.g.
 * `https://sub.domain.com/dpworld-tour/article/my-slug`.
 *
 * `getContentUrlPath` owns the per-content-type route mapping (Article/Quiz/Poll
 * live at `/<type>/<slug>`, VOD at `/video/...`, schema-based types under
 * `/content/...`), so the JSON-LD `url` can never drift from what the renderer
 * actually serves.
 */
export const buildContentUrl = (tagName: string, slug: string, brandRoute: string): string => {
  const origin = getSubDomain();
  if (!origin || !slug || !brandRoute) return "";
  return `${origin}/${brandRoute}${getContentUrlPath({ tagName, slug })}`;
};

/** Site-level `@id` the per-page nodes point at via `isPartOf`. */
export const getWebsiteId = (): string => {
  const origin = getSubDomain();
  return origin ? `${origin}/#website` : "";
};

/**
 * Markup carried forward verbatim when a content item is duplicated and its
 * type cannot be rebuilt from the duplicate payload (Quiz/VOD/Event — their
 * source fields arrive as document paths, not hydrated records).
 *
 * The copy is a NEW page at its OWN route, so the source's page identity must
 * not travel with it: a duplicate claiming `@id: <original>#quiz` /
 * `url: <original>` makes two live pages assert one canonical (KIWI-6280).
 * Root `@id` and `url` are stripped; everything else (asset URLs such as VOD
 * `contentUrl`/`embedUrl`, nested nodes) is kept. The editor's save effect
 * rebuilds the identity the first time the duplicate is opened and saved.
 *
 * Malformed or non-string input yields `{}` rather than throwing — a bad
 * `structure_data` string must not abort the whole duplicate (same fail-soft
 * rule as `readPublisherIdentity`).
 */
export const inheritStructureData = (raw: unknown): Record<string, any> => {
  if (typeof raw !== "string" || !raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const stripIdentity = (node: Record<string, any>) => {
      const { "@id": _id, url: _url, ...rest } = node;
      return rest;
    };
    // Envelope { use, auto, manual } — strip the page identity inside
    // each section, not at the (identity-free) envelope root.
    if (parsed.use === "auto" || parsed.use === "manual") {
      return {
        ...parsed,
        auto: parsed.auto && typeof parsed.auto === "object" ? stripIdentity(parsed.auto) : {},
        manual:
          parsed.manual && typeof parsed.manual === "object" ? stripIdentity(parsed.manual) : null,
      };
    }
    return stripIdentity(parsed);
  } catch {
    return {};
  }
};
